import type { ComponentProps } from 'react';
import { useMemo, useCallback } from 'react';
import { useTracking } from 'react-tracking';
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';
import parse, {
  domToReact,
  Element,
  attributesToProps,
} from 'html-react-parser';

import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { Text } from '@simplywallst/ui-core';
import { Heading } from '@simplywallst/ui-core/next';

import { Img, Ol, StyledLink, StyledUniversalLink, Ul } from './styled';
import { getRemappedHeadingsAccordingToBaseline } from '@/utilities/heading';

type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const BASELINE_HEADING_LEVEL = 2;

const EmbeddedImage = ({ src, alt }: { src: string; alt: string }) => {
  const coverImageSrc = useCloudflareImage({ src });
  if (!src) return null;
  return <Img src={coverImageSrc} alt={alt} />;
};

const headingTypographyMapping = (
  heading: HeadingType
): ComponentProps<typeof Heading>['size'] => {
  switch (heading) {
    case 'h1': // unsupported by RTE
    case 'h2': // unsupported by RTE
    case 'h3':
      return 'hero';
    case 'h4':
      return 'mega';
    case 'h5':
      return 'xLarge';
    case 'h6': // unsupported by RTE
      return 'small';
  }
};

export const useHtmlReactParser = ({ html }: { html: string }) => {
  const { trackEvent } = useTracking();
  const handleClick = useCallback(
    (e) => {
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'narrative_link',
        href: e.currentTarget.href,
      });
    },
    [trackEvent]
  );

  const parsedHtml = useMemo(() => {
    const headingMap = getRemappedHeadingsAccordingToBaseline(
      html,
      BASELINE_HEADING_LEVEL
    );
    const parseOptions: HTMLReactParserOptions = {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element) {
          switch (domNode.name) {
            case 'ul':
              return <Ul>{domToReact(domNode.children, parseOptions)}</Ul>;
            case 'ol':
              return <Ol>{domToReact(domNode.children, parseOptions)}</Ol>;
            case 'p':
              const parent = domNode.parent as Element;
              const parentName = parent !== null ? parent.name : '';
              return parentName === 'li' ? (
                <>{domToReact(domNode.children, parseOptions)}</>
              ) : (
                <Text as="p" typography="editorBody" color="lightGray">
                  {domToReact(domNode.children, parseOptions)}
                </Text>
              );
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              return (
                <Heading
                  className="text-pretty text-solid"
                  size={headingTypographyMapping(domNode.name)}
                  as={headingMap[domNode.name] ?? 'h2'}
                >
                  {domToReact(domNode.children, parseOptions)}
                </Heading>
              );
            case 'b':
            case 'strong':
              return (
                <Text
                  as="strong"
                  typography="editorBodyStrong"
                  color="lightGray"
                >
                  {domToReact(domNode.children, parseOptions)}
                </Text>
              );
            case 'i':
            case 'em':
              return (
                <Text as="em" typography="editorBody" color="lightGray">
                  {domToReact(domNode.children, parseOptions)}
                </Text>
              );
            case 'font':
              return <>{domToReact(domNode.children, parseOptions)}</>;
            case 'figure':
              return (
                <figure>{domToReact(domNode.children, parseOptions)}</figure>
              );
            case 'img': {
              const props = attributesToProps(domNode.attribs);
              return <EmbeddedImage src={props.src} alt={props.alt} />;
            }
          }

          if (domNode.name === 'a' && domNode.attribs.href) {
            if (domNode.attribs.href?.includes('mailto:')) {
              return (
                <StyledLink
                  href={domNode.attribs.href}
                  rel={'nofollow'}
                  onClick={handleClick}
                >
                  {domToReact(domNode.children, parseOptions)}
                </StyledLink>
              );
            }
            return (
              <StyledUniversalLink
                openedNewTab
                to={domNode.attribs.href}
                onClick={handleClick}
              >
                {domToReact(domNode.children, parseOptions)}
              </StyledUniversalLink>
            );
          }
        }
      },
    };

    return parse(html, parseOptions);
  }, [handleClick, html]);

  return parsedHtml;
};
